import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { globalActions, globalSlice } from '@redux/slices/states/global/globalSlice';
import { useAppDispatch } from '@redux/store';
import { DateTimeInput, Grid, Select, TextInput, Button, Icon } from '@ts/storybook';
import { CStatusIndicator, IForm } from '@ts/ui-models';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface ISelectedIndicatorTypes {
  id: string;
  statusIndicator?: CStatusIndicator | null;
  indicatorValue?: string | number | boolean;
}

interface IEGRStatusSelectionProps extends IForm {}

const EGRStatusSelection = (props: IEGRStatusSelectionProps) => {
  const { mode, values: EGR, onCallbackChange } = props;
  const isReadOnly = mode === 'view';
  const dispatch = useAppDispatch();
  const [state, setState] = useState<ISelectedIndicatorTypes[]>([...(EGR.selectedIndicatorTypes ?? []), { id: 'last' }]);

  //#region Handlers
  const onChangeStatusIndicator = (id: string, indicator: CStatusIndicator | null) => {
    if (state.find(ind => ind.statusIndicator && ind.id === indicator?.getID())) {
      dispatch(globalActions.notification('You cannot select the same indicator twice'));
      return;
    } else if (indicator !== null) {
      setState(
        state.map(ind =>
          ind.id === id ? { id: indicator.getID(), statusIndicator: indicator, indicatorValue: ind.indicatorValue } : ind
        )
      );
    }
  };

  const onChangeStatusValue = (id: string) => (value: string | number | boolean) => {
    setState(prev => {
      const updated = prev.map(ind => (ind.id === id ? { ...ind, indicatorValue: value } : ind));
      const withoutLast = updated.filter(ind => ind.id !== 'last');
      const lastIsLast = updated[updated.length - 1]?.id === 'last';
      return lastIsLast ? updated : [...withoutLast, { id: 'last' }];
    });
  };
  //#endregion

  //#region Render
  const inputStatusValue = (indicator: ISelectedIndicatorTypes) => {
    if (!indicator.statusIndicator) return;
    const value: any = indicator.indicatorValue;
    const { id, type, possibleValues } = indicator.statusIndicator;
    if (!id) return;

    switch (type) {
      case 'boolean':
      case 'list':
        return (
          <Select
            key={id}
            label="Value"
            value={value}
            onChange={v => onChangeStatusValue(id)(v as any)}
            isReadOnly={isReadOnly}
            options={possibleValues?.map((pv, pvIdx) => {
              return { key: uuidv4() + pvIdx, value: pv, label: String(pv) };
            })}
          />
        );

      case 'string':
        return (
          <TextInput
            key={id}
            label="Value"
            isReadOnly={isReadOnly}
            placeholder="Value"
            value={value}
            onChangeValue={onChangeStatusValue(id)}
          />
        );

      case 'integer':
      case 'float':
        return (
          <TextInput
            label="Value"
            type="number"
            key={id}
            isReadOnly={isReadOnly}
            placeholder="0"
            value={value}
            onChangeValue={onChangeStatusValue(id)}
          />
        );

      case 'date':
      case 'time':
      case 'timestamp':
        return (
          <DateTimeInput
            key={id}
            label="Value"
            isReadOnly={isReadOnly}
            onlyDate={type === 'date'}
            onlyTime={type === 'time'}
            value={value}
            onChange={v => onChangeStatusValue(id)(v as any)}
          />
        );
    }
  };

  const rowStatusSelection = (status: ISelectedIndicatorTypes, i: number) => {
    const statusValue = status.statusIndicator;
    const oldId = status.id;
    return (
      <Grid container size={{ xs: 12 }}>
        <Grid size={!statusValue ? 12 : 8}>
          <WrapperEntitySelector
            label={i === 0 ? 'Select status indicator' : 'Select other status indicator'}
            multiple={false}
            searchComponent={generateSearchEntitySelector('indicatorType')}
            value={status.statusIndicator ?? status.id}
            onChangeEntity={v => onChangeStatusIndicator(oldId, v && new CStatusIndicator(v as any))}
            isReadOnly={isReadOnly}
          />
        </Grid>
        {statusValue && <Grid size={'grow'}>{inputStatusValue(status)}</Grid>}
        {statusValue && (
          <Grid size={'auto'}>
            <Button
              color="error"
              ghost
              className="relative top-1"
              onClick={() => setState(state.filter(ind => ind.id !== status.id))}
              startIcon={<Icon icon="delete" className="relative left-1.5" />}
            />
          </Grid>
        )}
      </Grid>
    );
  };
  //#endregion

  useEffect(() => {
    onCallbackChange('selectedIndicatorTypes')(
      state
        .filter(ind => ind.id !== 'last')
        .map(ind => {
          const { statusIndicator, ...rest } = ind;
          return rest;
        })
    );
  }, [state]);

  const list = useMemo(() => (isReadOnly ? state.filter(item => item.id !== 'last') : state), [isReadOnly, state]);

  return (
    <Grid container spacing={2} alignItems="flex-start" size={12}>
      {list.map((item, i) => (
        <Grid size={{ xs: 12 }} container spacing={2} alignItems="flex-start" key={item.id}>
          {rowStatusSelection(item, i)}
        </Grid>
      ))}
    </Grid>
  );
};

export default EGRStatusSelection;
